/**
 * @fileoverview The one notice before a photo goes to the gallery, and the name to show.
 *
 * It stays your photo, we may show it, take it back any time. The name is
 * pre-filled from the profile the first time and remembered after; it can
 * be changed here on any later share. Confirming hands the name back to
 * whoever opened this, which does the sharing.
 *
 * The plant card's Public switch opens it the first time a plant goes public
 * (PublicPageSwitch), with its own title and words: that switch is the one
 * place a gardener decides whether their pictures are shown beyond their own
 * links, the website gallery included.
 *
 * Where: opened by the plant card's Public switch (share/PublicPageSwitch),
 * the first time a plant goes public, with that switch's own title and words.
 * API: none; the opener shares with the name it is handed.
 * Settings: the server's limits.GALLERY_NAME_MAX (lib/growSettings), the name's length.
 * The opener may hand its own notice as an element (the Public switch's
 * three bullets, PublicConsentBullets).
 * Words (en/es): gallery.share.title, .titleOne, .notice, .nameLabel,
 * .namePlaceholder, .nameHint, .confirm; sitewide.cancel, sitewide.saving (Button loading).
 * Foot (B111): ActionRow, Cancel left and the action right.
 *
 * @module GalleryConsentDialog
 */

import React from 'react';
import { ActionRow } from '@/shared/components/ui/ActionRow';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { useGrowLimits } from '@/shared/lib/growSettings';

/** The dialog's state, and what its two buttons do. */
interface GalleryConsentDialogProps {
  /** Whether the dialog is showing. */
  open: boolean;
  /** How many photos are about to be shared, for the button. */
  count: number;
  /** The name to start with: the saved one, or a guess from the profile. */
  initialName: string;
  /** True while the sharing runs: the buttons wait and the dialog will not close. */
  busy: boolean;
  /** Cancel, Escape or a tap outside (ignored while busy): nothing is shared. */
  onCancel: () => void;
  /** Confirmed: the name to show, trimmed; the opener does the sharing. */
  onConfirm: (name: string) => void;
  /** A heading in place of "Share N photos" (the Public switch's). */
  title?: string;
  /**
   * In place of the gallery's notice: a string (shown as the dialog's description)
   * or an element that is itself the description (a DialogDescription).
   */
  notice?: React.ReactNode;
  /** The confirm button's word in place of "Share". */
  confirmLabel?: string;
}

/** The gallery's one notice, with the name the photos will be shown under. */
export const GalleryConsentDialog: React.FC<GalleryConsentDialogProps> = ({
  open, count, initialName, busy, onCancel, onConfirm, title, notice, confirmLabel,
}) => {
  const { reactTranslate: t } = useTranslation();
  const [name, setName] = React.useState(initialName);
  const inputId = React.useId();

  const limits = useGrowLimits();
  // Each opening starts from the opener's name, not whatever was typed and cancelled last time.
  React.useEffect(() => { if (open) setName(initialName); }, [open, initialName]);

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next && !busy) onCancel(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title ?? (count === 1 ? t('gallery.share.titleOne') : t('gallery.share.title', { count }))}</DialogTitle>
          {notice !== undefined && typeof notice !== 'string'
            ? notice
            : <DialogDescription>{notice ?? t('gallery.share.notice')}</DialogDescription>}
        </DialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor={inputId}>{t('gallery.share.nameLabel')}</Label>
          <Input
            id={inputId}
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder={t('gallery.share.namePlaceholder')}
            maxLength={limits.GALLERY_NAME_MAX}
            autoFocus
          />
          <p className="text-xs text-muted-foreground">{t('gallery.share.nameHint')}</p>
        </div>
        <DialogFooter>
          <ActionRow onCancel={onCancel} disabled={busy}>
            <Button onClick={() => onConfirm(name.trim())} loading={busy} disabled={name.trim() === ''}>
              {confirmLabel ?? t('gallery.share.confirm')}
            </Button>
          </ActionRow>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
